import { SiteBuilderDialog } from "../../sitebuilder-dialog/SiteBuilderDialog";
import {consola} from "consola";
import type { SbDialogWrapper } from '../../../modules/web-components/sb-dialog-wrapper';

const disabledCheckboxIconClass = 'fas fa-fw fa-check fa-lg';
const enabledCheckboxIconClass = 'far fa-fw fa-check-square fa-2x';
const squareIconClass = 'fal fa-fw fa-square fa-2x';

type CookiePreferences = {
  functional: boolean,
  advertising: boolean,
  performance: boolean,
}

let storedPreferences: CookiePreferences | null = null;

/** The first time the cookie dialog is opened, this will make a note of what preferences we have initially, so we can
 * reset to properly support the dialog being closed and re-opened. */
function populateStoredPreferences(form: HTMLFormElement) {
  if (storedPreferences === null) {
    storedPreferences = {
      functional: form.querySelector<HTMLInputElement>('input#cookie-modal-functional-cookies').checked,
      advertising: form.querySelector<HTMLInputElement>('input#cookie-modal-advertising-cookies').checked,
      performance: form.querySelector<HTMLInputElement>('input#cookie-modal-performance-cookies').checked
    }
  }
}

/** Reset form to the saved preferences received from the server */
function resetForm(form: HTMLFormElement) {
  form.querySelector<HTMLInputElement>('input#cookie-modal-functional-cookies').checked = storedPreferences.functional;
  form.querySelector<HTMLInputElement>('input#cookie-modal-advertising-cookies').checked = storedPreferences.advertising;
  form.querySelector<HTMLInputElement>('input#cookie-modal-performance-cookies').checked = storedPreferences.performance;
}

/** Show the on hover ticks that appear on the big cookie banner that appears if a user has no saved cookie settings */
function showGhostlyAcceptAllTicks(form: HTMLFormElement) {
  form.querySelectorAll('.checkbox:not(.disabled)').forEach((checkbox) => {
    checkbox.classList.add('ghostTick');
  });
}

/** Hide the on hover ticks that appear on the big cookie banner that appears if a user has no saved cookie settings */
function hideGhostlyAcceptAllTicks(form: HTMLFormElement) {
  form.querySelectorAll('.checkbox:not(.disabled)').forEach((checkbox) => {
    checkbox.classList.remove('ghostTick');
  });
}

/** Update the classes that manage checkbox icon display based on the checked value of the hidden checkboxes */
function updateCheckboxes(form: HTMLFormElement) {
  form.querySelectorAll('.checkbox').forEach((checkbox) => {
    if (checkbox.querySelector<HTMLInputElement>('input[type="checkbox"]').checked) {
      checkbox.classList.add('selected');
    } else {
      checkbox.classList.remove('selected');
    }
  });
}

/** Add functionality and classes to the cookie preference form checkboxes */
function initialiseCookiePreferenceCheckboxes(form: HTMLFormElement) {
  if (form.dataset.initialised !== 'true') {
    const checkboxes = form.querySelectorAll('input[type="checkbox"]');

    checkboxes.forEach((checkbox) => {
      const container = checkbox.closest('.checkbox');
      const selectedIconType = container.classList.contains('disabled')
        ? disabledCheckboxIconClass
        : enabledCheckboxIconClass;
      checkbox.insertAdjacentHTML('afterend',
        `<i class="${selectedIconType}" aria-hidden="true"></i><i class="${squareIconClass}" aria-hidden="true"></i>`
      );
      checkbox.addEventListener('change', () => { updateCheckboxes(form) });
      checkbox.addEventListener('focus', () => { container.classList.add('focused') });
      checkbox.addEventListener('blur', () => { container.classList.remove('focused') });
    });

    updateCheckboxes(form);
    populateStoredPreferences(form);
    form.dataset.initialised = 'true';
  }
}

/** Will populate the dialog with the cookie form the first time its called. If cancelled, the dialog will reset the
 * form to the preferences it had the first time it was opened. */
function showCookieDialog() {
  let cookieDialogWrapper = document.querySelector<SbDialogWrapper>('#cookie-modal-wrapper');
  let cookieDialog = document.querySelector<HTMLDialogElement>('#cookie-modal');

  if (cookieDialog) {
    // SBTWO-12392 Horrible hack to make sure the second dialog is on top of the first one if it's relying on dialog-polyfill instead of native support
    const banner = document.querySelector<HTMLDialogElement>('#cookie-banner');
    if (banner?.style?.zIndex) {
      cookieDialog.style.zIndex = (parseInt(banner.style.zIndex, 10) + 1).toString();
    }
    // End of horrid SBTWO-12392 hack

    const form = cookieDialog.querySelector('form');
    resetForm(form)
    initialiseCookiePreferenceCheckboxes(form);
    updateCheckboxes(form);
    SiteBuilderDialog.whenWrapperDefined(() => {
      cookieDialogWrapper.showModal();
    });
  } else {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    fetch(`/sitebuilder2/user/cookiepreference/modal.htm?returnTo=${returnTo}`).then((response) => {
      response.text().then((responseText) => {
        document.querySelector('.cookie-preference-modal-container').innerHTML = responseText;
        cookieDialogWrapper = document.querySelector<SbDialogWrapper>('#cookie-modal-wrapper');
        cookieDialog = document.querySelector<HTMLDialogElement>('#cookie-modal');
        const form = cookieDialog.querySelector('form');
        initialiseCookiePreferenceCheckboxes(form);
        cookieDialog.querySelector('.cookie-preference-submit').addEventListener('click', () => {
          form.submit();
        });
        SiteBuilderDialog.whenWrapperDefined(() => {
          cookieDialog.showModal();
        });
      });
    })
  }
}

/** Call after DOMContentLoaded - intended for sitebuilder.ts */
export function init () {
  document.querySelectorAll('.cookie-banner-modal-button, #cookie-preference-link').forEach((link) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      showCookieDialog();
    });
  });

  // SBTWO-12265 Calculate the height of the cookie banner and set it as a CSS variable
  const bannerWrapper = document.querySelector<SbDialogWrapper>('#cookie-banner-wrapper');
  const banner = document.querySelector<HTMLDialogElement>('#cookie-banner');
  if (banner) {
    // set height to variable on resize
    const setBannerHeight = () => {
      document.documentElement.style.setProperty('--w-sys-cookieBanner-height', `${banner.offsetHeight}px`);
    };
    setBannerHeight();
    window.addEventListener('resize', setBannerHeight);
    SiteBuilderDialog.whenWrapperDefined(() => {
      bannerWrapper.showModal();
    });
  }

  const form = document.querySelector<HTMLFormElement>('#cookiePreferenceForm');
  if (form) {
    const allButton = form.querySelector('.btn-accept-all');
    allButton?.addEventListener('mouseover', () => {
      showGhostlyAcceptAllTicks(form);
    });
    allButton?.addEventListener('mouseout', () => {
      hideGhostlyAcceptAllTicks(form);
    });
    initialiseCookiePreferenceCheckboxes(form);
  }

  // Show the cookie policy in a separate dialog when clicked so it can be read before accepting cookies
  const cookiePolicyLink = document.querySelector<HTMLElement>('.cookie-policy-link a');
  if (cookiePolicyLink) {
    cookiePolicyLink?.addEventListener('click', async (event) => {
      event.preventDefault();
      let cookiePolicyDialog = document.querySelector<HTMLDialogElement>('#cookie-policy-dialog');
      if (!cookiePolicyDialog) {
        cookiePolicyDialog = SiteBuilderDialog.blankDialog({
          wrapperId: 'cookie-policy-dialog-wrapper',
          dialogId: 'cookie-policy-dialog',
          title: 'Cookie Policy',
          hideCloseButtonInFooter: true,
          content: '<p><i class="fas fa-spinner fa-pulse" aria-hidden="true"></i> Loading</p>'
        });
        let cookiePolicyDialogWrapper = document.querySelector<SbDialogWrapper>('#cookie-policy-dialog-wrapper');
        document.body.appendChild(cookiePolicyDialogWrapper);
        SiteBuilderDialog.whenWrapperDefined(() => {
          cookiePolicyDialogWrapper.showModal();
        });
        const dialogBody = cookiePolicyDialog.querySelector('.dialog-body');
        try {
          const response = await fetch(cookiePolicyLink.getAttribute('href') + '?xdoplain=true')
          const responseText = await response.text();
          dialogBody.innerHTML = '';
          dialogBody.insertAdjacentHTML('beforeend',
            new DOMParser().parseFromString(responseText, 'text/html').querySelector('.content-text').innerHTML
          );
        } catch (error) {
          dialogBody.innerHTML = '<p class="text-danger"><i class="fas fa-exclamation-triangle" aria-hidden="true"></i> There was an error loading the cookie policy. Refresh the page and try again.</p>';
          consola.error('Error loading cookie policy:', error);
        }
      } else {
        SiteBuilderDialog.whenWrapperDefined(() => {
          cookiePolicyDialog.showModal();
        });
      }
    });
  }
}
